import React, { useMemo, useState } from 'react';
import Donut from './Donut';
import { fmt$, signed$, pnlClr } from './fmt';

// Combined allocation + holdings view, adapted from the Maghaweer Portfolio v2
// AllocationBlock: one panel with a Security | Bucket | Sector toggle, a table on the left
// and a donut on the right, plus a book-wide P&L reconciliation strip at the bottom.
//
//   Security — the holdings themselves: Symbol · Allocation (bar + %) · Market Value · P&L.
//   Bucket   — by strategy class (Momentum, Trend Following, …): the book's opening rationale.
//   Sector   — by GICS sector.
//
// Simplified from source in one place: Maghaweer's Bucket mode renders a live
// class → portfolio → strategy tree; here Bucket is a FLAT grouping — same shape as the Sector
// table. Its rows come from `byBucket` (the feed's `buckets` array, or the illustrative book's
// grouping), never re-derived from a holding's strategy class. Everything else (the topN +
// Others donut collapse, the toggle, the Security columns) is faithful to the source.

// The palette leads with the brand token so the largest slice is the accent — cobalt (#3B82F6)
// on the light default theme, gold in dark. So the SECOND colour must avoid BOTH blue (or it
// merges into the light accent — the exact bug where slices 1 and 2 read as one colour) AND
// gold/amber (or it merges into the dark accent). Violet leads the rest, and amber is pushed late
// so it is never adjacent to the gold slice on a 2–3 slice chart (Bucket has just two).
const COLORS = ['rgb(var(--brand-gold-rgb))', '#8b5cf6', '#10b981', '#ec4899', '#06b6d4', '#ef4444', '#f59e0b', '#84cc16'];
const OTHERS = '#6b7280';
const GOLD = 'rgb(var(--brand-gold-rgb))';

// Allocation bar + % label — proportion bar (width = pct of the deployed book, min 2% so a
// small holding still shows a sliver) with the number right-aligned beside it.
const Bar = ({ pct }) => {
  const p = pct || 0;
  return (
    <div className="flex items-center gap-2">
      <span className="flex-1 h-[6px] rounded-full bg-gray-100 dark:bg-[#20272f] overflow-hidden">
        <span className="block h-full rounded-full" style={{ width: `${Math.max(p > 0 ? 2 : 0, Math.min(100, p))}%`, background: GOLD }} />
      </span>
      <span className="w-11 text-right tabular-nums text-gray-500 shrink-0">{p.toFixed(1)}%</span>
    </div>
  );
};

// Collapse [{label, value}] into top-N (+ Others), coloured, DESC by value — for the donut.
function topN(items, n = 6, colors = COLORS) {
  const clean = (items || []).filter((x) => x.value > 0).sort((a, b) => b.value - a.value);
  const head = clean.slice(0, n).map((x, i) => ({ ...x, color: colors[i % colors.length] }));
  const tail = clean.slice(n);
  if (tail.length) head.push({ label: `Others (${tail.length})`, value: tail.reduce((s, x) => s + x.value, 0), color: OTHERS });
  return head;
}

// Order: Bucket first (the book's own strategy structure — the most meaningful default view),
// then Sector (GICS), then Security (the individual names). Bucket is the landing tab.
const MODES = [
  { id: 'bucket', label: 'Bucket', center: 'Buckets' },
  { id: 'sector', label: 'Sector', center: 'Sectors' },
  { id: 'security', label: 'Security', center: 'Deployed' },
];

// The allocation view-model is passed in (the adapter builds it from either the live feed or the
// illustrative book), so this component never branches on the source. `holdings`, `bySector`,
// `byBucket` and `bookPnl` all carry a pre-derived allocation-% / total, computed once upstream.
export default function AllocationBlock({ holdings = [], bySector = [], byBucket = [], bookPnl = {} }) {
  const [mode, setMode] = useState('bucket');
  // Security lists every name and can run long, so it shows the top holdings + a combined
  // "Others" row that expands on demand. Bucket and Sector are short, flat lists shown in full.
  const [showAllSec, setShowAllSec] = useState(false);

  const donutSegs = useMemo(() => {
    if (mode === 'security') return topN(holdings.map((h) => ({ label: h.symbol, value: h.pct * 100 })), 6);
    if (mode === 'bucket') return topN(byBucket.map((b) => ({ label: b.label, value: b.pct * 100 })), 6);
    return topN(bySector.map((s) => ({ label: s.label, value: s.pct * 100 })), 6);
  }, [mode, holdings, byBucket, bySector]);

  const meta = MODES.find((m) => m.id === mode);
  const sub = mode === 'security'
    ? `By security · ${holdings.length} holdings`
    : mode === 'bucket' ? 'By strategy bucket' : 'By GICS sector';

  const Breakdown = () => {
    if (mode === 'security') {
      // The merged holdings table: Symbol (tight) · Allocation (bar + %) · Market Value · P&L.
      // Capped at the top TOP_SEC names; the remainder folds into one "Others" row (combined
      // allocation, market value and P&L) that expands on demand — so a long book does not run
      // off the page, but nothing is hidden: the tail is one click away and its totals are shown.
      const TOP_SEC = 10;                       // holdings arrive pre-sorted DESC by market value
      const top = holdings.slice(0, TOP_SEC);
      const rest = holdings.slice(TOP_SEC);
      const othersPct = rest.reduce((s, h) => s + (h.pct || 0), 0);
      const othersMV = rest.reduce((s, h) => s + (h.marketValue || 0), 0);
      const othersPnl = rest.reduce((s, h) => s + (h.pnl || 0), 0);

      const Row = (h) => (
        <tr key={h.symbol} className="border-b border-gray-100 dark:border-gray-800/50 hover:bg-gray-50 dark:hover:bg-[#1c2330]">
          <td className="py-2 font-semibold text-gray-800 dark:text-gray-200 truncate" title={h.name}>{h.symbol}</td>
          <td className="py-2 pr-3"><Bar pct={h.pct * 100} /></td>
          <td className="py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">{fmt$(h.marketValue)}</td>
          <td className={`py-2 text-right tabular-nums ${pnlClr(h.pnl)}`} title={`Realized ${signed$(h.realized)} · Unrealized ${signed$(h.unrealized)}`}>
            {h.pnl != null && h.pnl !== 0 ? signed$(h.pnl) : (h.pnl === 0 ? '$0' : '—')}
          </td>
        </tr>
      );

      return (
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-xs" style={{ tableLayout: 'fixed' }}>
            <colgroup><col style={{ width: '15%' }} /><col style={{ width: '37%' }} /><col style={{ width: '23%' }} /><col style={{ width: '25%' }} /></colgroup>
            <thead>
              <tr className="text-[9px] uppercase tracking-wider text-gray-400 border-b border-gray-100 dark:border-gray-800">
                <th className="text-left py-2">Symbol</th><th className="text-left py-2">Allocation</th><th className="text-right py-2">Market Value</th><th className="text-right py-2">Total P&amp;L</th>
              </tr>
            </thead>
            <tbody>
              {top.map(Row)}
              {rest.length > 0 && showAllSec && rest.map(Row)}
              {rest.length > 0 && (
                <tr onClick={() => setShowAllSec((v) => !v)}
                    className="cursor-pointer border-b border-gray-100 dark:border-gray-800/50 hover:bg-gray-50 dark:hover:bg-[#1c2330]">
                  <td className="py-2 font-medium text-gray-500 dark:text-gray-400 truncate">
                    <span className="inline-block w-2 h-2 rounded-sm mr-2 align-middle" style={{ background: OTHERS }} />
                    {showAllSec ? 'Show fewer' : `Others (${rest.length})`}
                  </td>
                  <td className="py-2 pr-3">{showAllSec ? null : <Bar pct={othersPct * 100} />}</td>
                  <td className="py-2 text-right tabular-nums text-gray-500 dark:text-gray-400">{showAllSec ? '' : fmt$(othersMV)}</td>
                  <td className={`py-2 text-right tabular-nums ${showAllSec ? 'text-brand-gold' : pnlClr(othersPnl)}`}>
                    {showAllSec ? '▴ collapse' : (othersPnl ? signed$(othersPnl) : '$0')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      );
    }

    // Bucket and Sector share a flat table: label · Allocation · Market Value.
    const rows = mode === 'bucket' ? byBucket : bySector;
    const heading = mode === 'bucket' ? 'Bucket' : 'Sector';
    return (
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-xs" style={{ tableLayout: 'fixed' }}>
          <colgroup><col style={{ width: '40%' }} /><col style={{ width: '34%' }} /><col style={{ width: '26%' }} /></colgroup>
          <thead>
            <tr className="text-[9px] uppercase tracking-wider text-gray-400 border-b border-gray-100 dark:border-gray-800">
              <th className="text-left py-2">{heading}</th><th className="text-left py-2">Allocation</th><th className="text-right py-2">Market Value</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.label} className="border-b border-gray-100 dark:border-gray-800/50 hover:bg-gray-50 dark:hover:bg-[#1c2330]">
                <td className="py-2 text-gray-700 dark:text-gray-300 truncate" title={r.label}>
                  <span className="inline-block w-2 h-2 rounded-sm mr-2 align-middle" style={{ background: COLORS[i % COLORS.length] }} />{r.label}
                </td>
                <td className="py-2 pr-3"><Bar pct={r.pct * 100} /></td>
                <td className="py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">{fmt$(r.marketValue)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4">
      {/* flex-wrap so the 3-pill toggle drops below the title at 375px instead of forcing
          horizontal overflow inside the card. */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <h3 className="text-sm font-serif text-gray-800 dark:text-gray-200">Allocation &amp; Holdings</h3>
        <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 p-0.5 bg-gray-50 dark:bg-[#0d1117]">
          {MODES.map((m) => (
            <button key={m.id} onClick={() => setMode(m.id)}
              className={`px-3 py-1 text-[11.5px] rounded-md transition-colors ${mode === m.id ? 'bg-brand-gold text-white font-semibold' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}>
              {m.label}
            </button>
          ))}
        </div>
      </div>
      <div className="text-[10.5px] uppercase tracking-wide text-gray-400 mb-3">{sub}</div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.55fr_1fr] gap-4 items-stretch">
        <Breakdown />
        <div className="flex items-center justify-center">
          {donutSegs.length === 0
            ? <div className="text-gray-400 italic text-xs py-6">No data to chart</div>
            : <Donut segments={donutSegs} centerLabel={meta.center} size={220} />}
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-[11px]">
        <span className="text-gray-400 uppercase tracking-wide">Book P&amp;L · all names</span>
        <span className="tabular-nums text-gray-500">
          Realized <span className={pnlClr(bookPnl.realized)}>{signed$(bookPnl.realized)}</span>
          {' + '}Unrealized <span className={pnlClr(bookPnl.unrealized)}>{signed$(bookPnl.unrealized)}</span>
          {' = '}<span className={`font-semibold ${pnlClr(bookPnl.total)}`}>{signed$(bookPnl.total)}</span>
        </span>
      </div>
    </div>
  );
}
